import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      // Headings
      h1: 'text-3xl md:text-5xl font-medium',
      h2: 'text-2xl md:text-4xl leading-tight font-medium',
      h3: 'text-xl md:text-2xl font-medium',
      h4: 'text-lg md:text-xl font-medium',
      h5: 'text-lg font-medium leading-tight',

      // Body text
      body: 'text-base',
      bodySmall: 'text-sm text-slate-900/70',
      lead: 'text-lg text-slate-400',
      label: 'text-lg font-medium',
      small: 'text-xs text-slate-400',
    },
  },
})

type TypographyElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'

type TypographyVariant = NonNullable<VariantProps<typeof typographyVariants>['variant']>

const defaultElements: Record<TypographyVariant, TypographyElement> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  body: 'p',
  bodySmall: 'p',
  lead: 'p',
  label: 'span',
  small: 'span',
}

interface TypographyProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof typographyVariants> {
  as?: TypographyElement
}

function Typography({ className, variant, as, ...props }: TypographyProps) {
  const Component = as ?? (variant ? defaultElements[variant] : 'p')

  return <Component className={cn(typographyVariants({ variant }), className)} {...props} />
}

export { Typography }
